import * as React from 'react'
import { createBrowserRouter, Navigate } from 'react-router'
import { Loader2 } from 'lucide-react'
import { ProtectedRoute } from '@/auth/ProtectedRoute'
import { TenantProtectedRoute } from '@/auth/TenantProtectedRoute'
import { OpsLayout } from '@/components/OpsLayout'
import { OpsViewAsLayout } from '@/components/OpsViewAsLayout'
import { TenantLayout } from '@/components/TenantLayout'
import { DashboardEnvelopeAccess, OpsEnvelopeAccess } from '@/envelopes/EnvelopeAccess'
import { AcceptInvitePage } from '@/pages/AcceptInvitePage'
import { ConnectPage } from '@/pages/ConnectPage'
import { ConnectedAppsPage } from '@/pages/ConnectedAppsPage'
import { ActivityPage } from '@/pages/ActivityPage'
import { ApiKeysPage } from '@/pages/ApiKeysPage'
import { EnvelopeDetailPage } from '@/pages/EnvelopeDetailPage'
import { ConfirmTestMailPage } from '@/pages/ConfirmTestMailPage'
import { EnvelopesPage } from '@/pages/EnvelopesPage'
import { ForgotPasswordPage } from '@/pages/ForgotPasswordPage'
import { MailboxPage } from '@/pages/MailboxPage'
import { McpConfirmPage } from '@/pages/McpConfirmPage'
import { OpsDesignPage } from '@/pages/OpsDesignPage'
import { OpsConnectedAppsPage } from '@/pages/OpsConnectedAppsPage'
import { OpsLoginPage } from '@/pages/OpsLoginPage'
import { OpsRecognizedAppsPage } from '@/pages/OpsRecognizedAppsPage'
import { OpsTenantDetailPage } from '@/pages/OpsTenantDetailPage'
import { OpsTenantEditPage } from '@/pages/OpsTenantEditPage'
import { OpsTenantNewPage } from '@/pages/OpsTenantNewPage'
import { OpsTenantsPage } from '@/pages/OpsTenantsPage'
import { OpsPricingPage } from '@/pages/OpsPricingPage'
import { BillingCardPage } from '@/pages/BillingCardPage'
import { BillingPayPage } from '@/pages/BillingPayPage'
import { QaPage } from '@/pages/QaPage'
import { ResetPasswordPage } from '@/pages/ResetPasswordPage'
import { SettingsPage } from '@/pages/SettingsPage'
import { BillingPage } from '@/pages/BillingPage'
import { SignPage } from '@/pages/SignPage'
import { TeamPage } from '@/pages/TeamPage'
import { TenantLoginPage } from '@/pages/TenantLoginPage'
import { RequestsPage } from '@/pages/RequestsPage'
import { WebhooksPage } from '@/pages/WebhooksPage'
import { RouteErrorPage } from '@/components/RouteErrorPage'

const ApiDocsPage = React.lazy(async () => {
  const module = await import('@/pages/ApiDocsPage')
  return { default: module.ApiDocsPage }
})

/**
 * Application routes.
 *
 * - Tenant dashboard: `/` (sign-in at `/login`, plus password reset and
 *   invitation acceptance), guarded by the tenant session.
 * - Staff ops console: `/ops/*` (sign-in at `/ops/login`), guarded by the staff
 *   session. The two sessions are independent. `/ops/tenants/:id/view/*`
 *   renders the tenant's own dashboard pages read-only for staff.
 * - Public: `/docs`, and the signer and answerer pages (`/sign`, `/embed`,
 *   `/qa`), which authenticate by their unguessable link.
 */
export const router = createBrowserRouter([
  {
    // Every route: a page that throws while rendering shows RouteErrorPage (and is reported).
    errorElement: <RouteErrorPage />,
    children: [
      { path: '/login', element: <TenantLoginPage /> },
      { path: '/forgot-password', element: <ForgotPasswordPage /> },
      { path: '/reset-password/:token', element: <ResetPasswordPage /> },
      { path: '/invite/:token', element: <AcceptInvitePage /> },
      { path: '/connect/:requestId', element: <ConnectPage /> },
      { path: '/mcp/confirm/:confirmationId', element: <McpConfirmPage /> },
      { path: '/billing/pay/:tenantId/:purchaseId', element: <BillingPayPage /> },
      { path: '/billing/card/:tenantId/:setupId', element: <BillingCardPage /> },
      { path: '/test-mail/confirm/:token', element: <ConfirmTestMailPage /> },
      {
        path: '/docs',
        element: (
          <React.Suspense fallback={<DocsFallback />}>
            <ApiDocsPage />
          </React.Suspense>
        ),
      },
      { path: '/sign/:token', element: <SignPage /> },
      { path: '/embed/:token', element: <SignPage /> },
      { path: '/qa/:token', element: <QaPage /> },
      { path: '/ops/login', element: <OpsLoginPage /> },
      { path: '/ops/forgot-password', element: <ForgotPasswordPage audience="ops" /> },
      { path: '/ops/reset-password/:token', element: <ResetPasswordPage audience="ops" /> },
      {
        path: '/ops',
        element: <ProtectedRoute />,
        children: [
          {
            element: <OpsLayout />,
            children: [
              { index: true, element: <Navigate to="/ops/envelopes" replace /> },
              {
                element: <OpsEnvelopeAccess />,
                children: [
                  { path: 'envelopes', element: <EnvelopesPage /> },
                  { path: 'envelopes/:id', element: <EnvelopeDetailPage /> },
                ],
              },
              { path: 'tenants', element: <OpsTenantsPage /> },
              { path: 'pricing', element: <OpsPricingPage /> },
              { path: 'tenants/new', element: <OpsTenantNewPage /> },
              { path: 'tenants/:id', element: <OpsTenantDetailPage /> },
              { path: 'tenants/:id/edit', element: <OpsTenantEditPage /> },
              {
                path: 'tenants/:tenantId/view',
                element: <OpsViewAsLayout />,
                children: [
                  { index: true, element: <Navigate to="envelopes" replace /> },
                  {
                    element: <DashboardEnvelopeAccess />,
                    children: [
                      { path: 'envelopes', element: <EnvelopesPage /> },
                      { path: 'envelopes/:id', element: <EnvelopeDetailPage /> },
                    ],
                  },
                  { path: 'team', element: <TeamPage /> },
                  { path: 'api-keys', element: <ApiKeysPage /> },
                  { path: 'settings', element: <SettingsPage /> },
                  { path: 'billing', element: <BillingPage /> },
                  { path: 'webhooks', element: <WebhooksPage /> },
                  { path: 'requests', element: <RequestsPage /> },
                  { path: 'mailbox', element: <MailboxPage /> },
                  { path: 'activity', element: <ActivityPage /> },
                  { path: 'connected-apps', element: <ConnectedAppsPage /> },
                ],
              },
              { path: 'connected-apps', element: <OpsConnectedAppsPage /> },
              { path: 'recognized-apps', element: <OpsRecognizedAppsPage /> },
              { path: 'design', element: <OpsDesignPage /> },
            ],
          },
        ],
      },
      {
        element: <TenantProtectedRoute />,
        children: [
          {
            element: <TenantLayout />,
            children: [
              { index: true, element: <Navigate to="/envelopes" replace /> },
              {
                element: <DashboardEnvelopeAccess />,
                children: [
                  { path: 'envelopes', element: <EnvelopesPage /> },
                  { path: 'envelopes/:id', element: <EnvelopeDetailPage /> },
                ],
              },
              { path: 'team', element: <TeamPage /> },
              { path: 'api-keys', element: <ApiKeysPage /> },
              { path: 'settings', element: <SettingsPage /> },
              { path: 'billing', element: <BillingPage /> },
              { path: 'webhooks', element: <WebhooksPage /> },
              { path: 'requests', element: <RequestsPage /> },
              { path: 'mailbox', element: <MailboxPage /> },
              { path: 'activity', element: <ActivityPage /> },
              { path: 'connected-apps', element: <ConnectedAppsPage /> },
            ],
          },
        ],
      },
      { path: '*', element: <Navigate to="/" replace /> },
    ],
  },
])

function DocsFallback() {
  return (
    <div className="flex min-h-svh items-center justify-center bg-background">
      <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
    </div>
  )
}
